import { Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormikContext } from 'formik';
import React from 'react';
import { useIntl } from 'react-intl';

import { FormikDropdownField, FormikTextField } from '@components/FormikFields';

import { getObservationConfig } from '@constants/fhir';

import { useParameter } from '@hooks/useParameter';

import {
  type FormValues,
  ParameterExpression,
  ParameterExpressionMap,
  type ParameterValue,
} from '@models/reference-range';

const { Between, Gt, Gte, Lt, Lte } = ParameterExpression;

interface BoundFormProps {
  index: number;
  parameter: ParameterValue;
}

export default function BoundForm({ index, parameter }: BoundFormProps) {
  const { expression, lower_bound, upper_bound } = parameter;
  const { formatMessage } = useIntl();
  const { breakpoints } = useTheme();
  const matches = useMediaQuery(breakpoints.down(450));
  const { code } = useParameter();
  const formik = useFormikContext<FormValues>();
  const { setFieldValue } = formik;

  const expressionOptions = Array.from(ParameterExpressionMap.entries()).map(
    ([key, value]) => ({ k: key, v: formatMessage({ id: value }) })
  );
  const unit = getObservationConfig(code)?.unit;

  return (
    <Stack
      alignItems="center"
      direction={matches ? 'column' : 'row'}
      spacing={1}
      justifyContent="center"
    >
      <FormikDropdownField
        name={`parameters.${index}.expression`}
        onChange={(value) => {
          if (value === Gt || value === Gte) {
            setFieldValue(`parameters.${index}.upper_bound`, null);
          } else if (value === Lt || value === Lte) {
            setFieldValue(`parameters.${index}.lower_bound`, null);
          }
        }}
        options={expressionOptions}
        required
      />
      {(expression === Gt || expression === Gte || expression === Between) && (
        <FormikTextField
          endAdornment={unit}
          name={`parameters.${index}.lower_bound`}
          placeholder={lower_bound ? String(lower_bound) : undefined}
          type="number"
        />
      )}

      {(expression === Lt || expression === Lte || expression === Between) && (
        <FormikTextField
          endAdornment={unit}
          name={`parameters.${index}.upper_bound`}
          placeholder={upper_bound ? String(upper_bound) : undefined}
          type="number"
        />
      )}
    </Stack>
  );
}
